<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmCancelOrderDialog"
            :data="currentOrder"
            :action-loading="actionLoading.cancelOrder"
            @on-confirm="cancelOrder"
            @on-abort="closeConfirmCancelOrderDialog"
        >
            <template #title>
                Anulowanie zamówienia
            </template>
            <template #content>
                Czy na pewno chcesz anulować zamówienie?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <page-title>Lista zamówień eCommerce</page-title>

        <v-row align="center">
            <v-col cols="12" offset-lg="4" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="isPlannedSendDateTodaySelected"
                                label="Wysyłka na dziś"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col sm="auto" class="d-flex align-center">
                            <v-menu
                                offset-y
                                :close-on-content-click="false"
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        class="white--text"
                                        :color="'teal'"
                                        v-on="on"
                                    >
                                        Pokaż status
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item v-for="(item, index) in statuses" :key="index">
                                        <v-list-item-action>
                                            <v-checkbox
                                                :input-value="item.checked"
                                                color="deep-purple accent-4"
                                                @change="updateStatusFilter(item.id)"
                                            />
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title> {{ item.name }} </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="filters.productionNumber"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('productionNumber')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="orders"
                        :loading="ordersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="ordersData"
                        @pagination="updatePagination"
                    >
                        <template #item.productionNumber="{item}">
                            {{ item.productionNumber }}
                        </template>
                        <template #item.firstProductName="{item}">
                            <template v-if="item.firstProductName !== 'N/D'">
                                {{ item.firstProductName }}
                                <v-chip
                                    v-if="item.numberOfProducts > 1"
                                    class="ma-1"
                                    small
                                    color="warning"
                                >
                                    +1
                                </v-chip>
                            </template>
                            <template v-else>
                                {{ item.shopProductName }}
                            </template>
                        </template>
                        <template #item.customerName="{item}">
                            <template v-if="item.customer">
                                {{ item.customer.name }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.plannedSendDate="{item}">
                            {{ formatDate(item.plannedSendDate) }}
                        </template>
                        <template #item.amount="{item}">
                            <template v-if="item.firstProductAmount">
                                {{ item.firstProductAmount }}
                            </template>
                            <template v-else>
                                {{ item.quantity }}
                            </template>
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}

                            <v-tooltip v-if="item.overdue" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-icon color="warning" small v-on="on">
                                        mdi-clock-outline
                                    </v-icon>
                                </template>
                                <span>Opóźnione</span>
                            </v-tooltip>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="openOrder(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Anuluj"
                                            :disabled="item.statusId !== 10"
                                            v-on="on"
                                            @click="openConfirmCancelOrderDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-cart-remove
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Anuluj</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import router from '@/router'

import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'

import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ECommerceOrders',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('orders/getOrderStatuses')
            .then(response => {
                next(vm => {
                    vm.statuses = response.data.results.map(item =>
                        item.id === 10 || item.id === 15 ? {...item, checked: true} : {...item, checked: false} )
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearEcommerceOrdersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            statuses: [],
            isPlannedSendDateTodaySelected: false,
            confirmCancelOrderDialog: false,
            currentOrder: {},
            actionLoading: {
                cancelOrder: false
            },
            headers: [
                {
                    text: 'Numer zamówienia',
                    value: 'productionNumber',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Nazwa produktu',
                    value: 'firstProductName',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Klient',
                    value: 'customerName',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Data zamówienia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Planowana data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'right',
                    width: 120
                }
            ]
        }
    },
    computed: {
        orders() {
            return this.$store.state.orders.order.orders
        },
        ordersLoading() {
            return this.$store.state.orders.order.ordersLoading
        },
        savedFilters() {
            return this.$store.state.orders.order.ecommerceOrdersFilters
        },
        selectedOrderId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getOrders()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedOrderId) {
                return 'isSelected'
            }
        },
        getOrders() {
            this.dateRangeHandle()
            this.$store.dispatch('orders/getOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openOrder(orderId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(orderId)
            router.push({
                name: 'ordersEcommerceOrdersOrder',
                params: {
                    id: orderId
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        cancelOrder(order) {
            this.actionLoading.cancelOrder = true
            this.$store.dispatch('orders/updateOrder', {
                id: order.id,
                statusId: 120 // Anulowane
            })
                .then(() => {
                    this.getOrders()
                    this.closeConfirmCancelOrderDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.cancelOrder = false
                })
        },
        openConfirmCancelOrderDialog(order) {
            this.confirmCancelOrderDialog = true
            this.currentOrder = order
        },
        closeConfirmCancelOrderDialog() {
            this.confirmCancelOrderDialog = false
            this.currentOrder = {}
        },
        dateRangeHandle() {
            if (this.isPlannedSendDateTodaySelected) {
                this.filters.plannedSendDate__gte = this.todayMinDate
                this.filters.plannedSendDate__lte = this.todayMaxDate
            } else {
                this.filters.plannedSendDate__gte = ''
                this.filters.plannedSendDate__lte = ''
            }
        },
        updateStatusFilter(id) {
            let selectedOrderStatuses = this.filters.status_id != '' ? this.filters.status_id.split(',') : []

            const index = selectedOrderStatuses.findIndex(item => item == id)

            if (index > -1) {
                selectedOrderStatuses.splice(index, 1)
            } else {
                selectedOrderStatuses.push(id)
            }

            this.filters.status_id = selectedOrderStatuses.length > 0 ? selectedOrderStatuses.join(',') : ''
            this.changeFilter()
        },
        changeFilter() {
            this.$store.dispatch('orders/setEcommerceOrdersFilters', this.filters)
            this.resetPagination()
            this.getOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOrders()
            }
            this.clearPaginationPending()
        },
    }
}
</script>
